import React from 'react';
import {
  Avatar,
  Card,
  CardActionArea,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

const severityStyles = {
  alert: (theme) => ({
    boxShadow: `0 10px 26px -18px ${alpha(theme.palette.error.main, 0.55)}`,
    border: `1px solid ${alpha(theme.palette.error.main, 0.45)}`,
  }),
  warning: (theme) => ({
    boxShadow: `0 10px 26px -18px ${alpha(theme.palette.warning.main, 0.45)}`,
    border: `1px solid ${alpha(theme.palette.warning.main, 0.35)}`,
  }),
  normal: (theme) => ({
    boxShadow: '0 6px 18px rgba(26, 31, 54, 0.08)',
    border: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
  }),
};

const resolveColor = (theme, paletteRef = 'primary') => {
  if (typeof paletteRef !== 'string') {
    return paletteRef;
  }
  if (paletteRef.includes('.')) {
    const [base, shade] = paletteRef.split('.');
    const palette = theme.palette[base];
    return palette?.[shade] || palette?.main || paletteRef;
  }
  const palette = theme.palette[paletteRef];
  if (!palette) {
    return paletteRef;
  }
  return palette.main ?? paletteRef;
};

const MetricTile = ({
  label,
  value,
  subtext,
  icon,
  color = 'primary',
  severity = 'normal',
  highlight = false,
  onClick,
  tooltip,
}) => {
  const getStyles = (theme) => {
    const highlightColor = resolveColor(theme, color);
    if (severity !== 'normal') {
      return severityStyles[severity]?.(theme) ?? severityStyles.normal(theme);
    }
    if (highlight) {
      return {
        boxShadow: `0 14px 32px -20px ${alpha(highlightColor, 0.45)}`,
        border: `1px solid ${alpha(highlightColor, 0.28)}`,
      };
    }
    return severityStyles.normal(theme);
  };

  const cardContent = (
    <Stack direction="row" alignItems="center" spacing={2}>
      <Avatar
        variant="rounded"
        sx={(theme) => {
          const resolved = resolveColor(theme, color);
          return {
            bgcolor: alpha(resolved, 0.16),
            color: resolved,
            width: 48,
            height: 48,
            '& svg': { fontSize: 24 },
          };
        }}
      >
        {icon}
      </Avatar>
      <Stack spacing={0.5}>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" fontWeight={600}>
          {value}
        </Typography>
        {subtext && (
          <Typography variant="caption" color="text.secondary">
            {subtext}
          </Typography>
        )}
      </Stack>
    </Stack>
  );

  return (
    <Card
      sx={(theme) => ({
        borderRadius: '12px !important',
        background: theme.palette.background.paper,
        width: '100%',
        minHeight: 112,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
        ...getStyles(theme),
        transition: 'box-shadow 0.2s ease',
        '&:hover': onClick
          ? {
              boxShadow: `0 20px 36px -22px ${alpha(resolveColor(theme, color), 0.45)}`,
            }
          : undefined,
      })}
    >
      <Tooltip title={tooltip || subtext || ''}>
        <CardActionArea
          onClick={onClick}
          disabled={!onClick}
          sx={{
            borderRadius: '12px !important',
            p: 3,
            display: 'block',
            textAlign: 'left',
            width: '100%',
            cursor: onClick ? 'pointer' : 'default',
          }}
        >
          {cardContent}
        </CardActionArea>
      </Tooltip>
    </Card>
  );
};

export default MetricTile;
